import type { HseReportType } from '@felix/domain';
import { useState } from 'react';
import { useShell } from '../../shell-context.js';
import { CompanyNcList, CompanyNcRecord, NcSegment } from './company-nc/CompanyNc.js';
import { RequestDialog, TypeChoiceDialog } from './Dialogs.js';
import { HseList } from './HseList.js';
import { HseRecord } from './HseRecord.js';
import { type HseRoute, hsePath } from './routes.js';
import type { HseSource } from './source.js';

export interface HseScreenProps {
  source: HseSource;
  /**
   * Türün eski yolu (ör. `/compliance/non-conformity`): liste o türle süzülü açılır, yeni rapor o
   * türdür. `null`: HSEQ Reports (dört tür; ADR 0097 §1).
   */
  type: HseReportType | null;
  /** Sorgu dizesinden (`hseRoute`). */
  route: HseRoute;
}

/**
 * Compliance ▸ HSEQ Reports (ortak 2026-10-20b H1; ADR 0049 §11, ADR 0097): dört tür tek listede,
 * yeni rapor tür seçimiyle, kayıt 10 basamaklı form. Görünüm yolun sorgu dizesinden gelir; ekran
 * gemi uygulamasında ve web ofiste aynıdır (ADR 0019 §2).
 */
export function HseScreen({ source, type, route }: HseScreenProps) {
  const shell = useShell();
  const [requesting, setRequesting] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [vessels, setVessels] = useState<{ id: string; name: string }[]>([]);
  const go = (next: HseRoute) => shell?.navigate(hsePath(type, next));
  // Ofisin şirket uygunsuzluğu (ADR 0062 §2): yalnız ofiste, uygunsuzluk ve HSEQ listesinde
  const company = type === 'non_conformity' || type === null ? source.companyNc : undefined;
  const goCompany = (next: HseRoute) => shell?.navigate(hsePath('non_conformity', next));
  const segment = (value: 'vessel' | 'company') =>
    company ? (
      <NcSegment
        value={value}
        onChange={(next) =>
          next === 'company' ? goCompany({ view: 'company' }) : go({ view: 'list' })
        }
      />
    ) : null;
  const newReport = (chosen: HseReportType, requestId: string | null = null) =>
    go({ view: 'new', parentId: null, requestId, type: chosen });

  if (company && (route.view === 'companyNew' || route.view === 'companyDetail')) {
    const id = route.view === 'companyDetail' ? route.id : null;
    return (
      <CompanyNcRecord
        key={id ?? 'new'}
        source={company}
        output={source}
        id={id}
        onBack={() => goCompany({ view: 'company' })}
        onOpen={(next) => goCompany({ view: 'companyDetail', id: next })}
      />
    );
  }
  if (company && route.view === 'company') {
    return (
      <CompanyNcList
        source={company}
        segment={segment('company')}
        onOpen={(id) => goCompany({ view: 'companyDetail', id })}
        onNew={() => goCompany({ view: 'companyNew' })}
      />
    );
  }

  if (route.view === 'new' || route.view === 'detail') {
    const id = route.view === 'detail' ? route.id : null;
    const newType = route.view === 'new' ? (route.type ?? type ?? 'non_conformity') : type;
    return (
      <HseRecord
        key={id ?? `new-${newType}-${route.view === 'new' ? (route.copyId ?? '') : ''}`}
        source={source}
        type={newType}
        id={id}
        parentId={route.view === 'new' ? route.parentId : null}
        requestId={route.view === 'new' ? route.requestId : null}
        defectId={route.view === 'new' ? (route.defectId ?? null) : null}
        copyId={route.view === 'new' ? (route.copyId ?? null) : null}
        tab={route.view === 'detail' ? route.tab : null}
        onBack={() => go({ view: 'list' })}
        onOpen={(next, tab) => go({ view: 'detail', id: next, tab: tab ?? null })}
        onNewChild={(parentId) =>
          shell?.navigate(hsePath('deficiency', { view: 'new', parentId, requestId: null }))
        }
        onNavigate={(path) => shell?.navigate(path)}
        onCopy={(copyType, copyId) =>
          go({ view: 'new', parentId: null, requestId: null, type: copyType, copyId })
        }
      />
    );
  }
  return (
    <>
      <HseList
        source={source}
        type={type}
        segment={segment('vessel')}
        onOpen={(id) => go({ view: 'detail', id, tab: null })}
        onNew={(request) => {
          if (request) newReport(request.type, request.id);
          else if (type) newReport(type);
          else setChoosing(true);
        }}
        {...(source.office
          ? {
              onRequest: () => {
                void source
                  .list(type)
                  .then((list) => setVessels(list.vessels ?? []))
                  .catch(() => setVessels([]));
                setRequesting(true);
              },
            }
          : {})}
      />
      <TypeChoiceDialog
        opened={choosing}
        onClose={() => setChoosing(false)}
        onChoose={(chosen) => {
          setChoosing(false);
          newReport(chosen);
        }}
      />
      {source.office && requesting && vessels.length > 0 ? (
        <RequestDialog
          opened
          type={type ?? 'non_conformity'}
          vessels={vessels}
          onClose={() => setRequesting(false)}
          onConfirm={(input) => source.office?.request(input) ?? Promise.resolve()}
        />
      ) : null}
    </>
  );
}
